import { useEffect, useMemo, useState } from 'react';
import { collection, onSnapshot, query } from 'firebase/firestore';
import { db } from '../firebase';
import type { MaterialRateItem } from '../pages/OfficeEstimationPage';
import { mergeMaterialItem, type LegacyItem } from './materialLibrary';
import { LEGACY_COLLECTION, MASTER_COLLECTION } from './materialLibraryStore';

export type LibraryMode =
  | 'loading'
  | 'shared'   // the shared library is in use (or empty and ready)
  | 'legacy';  // only the old per-project copy exists: shown read-only until an admin migrates it

export type MaterialLibrary = {
  items: MaterialRateItem[];
  mode: LibraryMode;
  /** The old per-project copy, used to run the migration. */
  legacyItems: LegacyItem[];
  sharedCount: number;
};

const byOrder = (a: any, b: any) => Number(a?.order || 0) - Number(b?.order || 0);

/**
 * The material rate library for a project: shared materials merged with this project's own quotes,
 * selections and notes. Until the shared library exists, the old per-project copy is shown read-only.
 */
export function useMaterialLibrary(projectId: string | null | undefined): MaterialLibrary {
  const [master, setMaster] = useState<Map<string, Record<string, any>> | null>(null);
  const [rates, setRates] = useState<Map<string, Record<string, any>>>(new Map());
  const [legacy, setLegacy] = useState<LegacyItem[] | null>(null);

  useEffect(() => {
    if (!projectId) { setMaster(new Map()); return; }
    return onSnapshot(query(collection(db, MASTER_COLLECTION)), (snapshot) => {
      setMaster(new Map(snapshot.docs.map((item) => [item.id, item.data() as Record<string, any>])));
    }, () => setMaster(new Map()));
  }, [projectId]);

  useEffect(() => {
    if (!projectId) { setRates(new Map()); return; }
    return onSnapshot(query(collection(db, 'projects', projectId, 'materialRates')), (snapshot) => {
      setRates(new Map(snapshot.docs.map((item) => [item.id, item.data() as Record<string, any>])));
    }, () => setRates(new Map()));
  }, [projectId]);

  useEffect(() => {
    if (!projectId) { setLegacy([]); return; }
    return onSnapshot(query(collection(db, 'projects', projectId, LEGACY_COLLECTION)), (snapshot) => {
      setLegacy(snapshot.docs.map((item) => ({ id: item.id, data: item.data() as Record<string, any> })));
    }, () => setLegacy([]));
  }, [projectId]);

  return useMemo<MaterialLibrary>(() => {
    if (master === null || legacy === null) return { items: [], mode: 'loading', legacyItems: legacy || [], sharedCount: 0 };
    if (master.size === 0 && legacy.length > 0) {
      return { items: legacy.map((item) => ({ ...item.data, id: item.id }) as unknown as MaterialRateItem).sort(byOrder), mode: 'legacy', legacyItems: legacy, sharedCount: 0 };
    }
    const items = Array.from(master.entries()).map(([id, data]) => mergeMaterialItem(id, data, rates.get(id))).sort(byOrder);
    return { items, mode: 'shared', legacyItems: legacy, sharedCount: master.size };
  }, [master, rates, legacy]);
}
